/**
 * Upload Studio refusals, shown in the operator's own language (Phase 91).
 *
 * Every code here is registered in the backend's FORWARDABLE_CAUSE_CODES, so
 * it reaches the browser as `err.data.cause.code` (plus a short reason enum or
 * a count). The page never shows the server's own text: an error with no
 * registered code reads as the generic line (T-91-28).
 *
 * Keys carry their namespace (`uploadStudio:` or `productSync:`), so a caller
 * passes the answer straight to `t()` from `useTranslation('uploadStudio')`.
 */

export interface StudioErrorLine {
  /** A namespaced i18n key. */
  key: string;
  /** Interpolation values (a count, for a plural key). */
  values?: Record<string, string | number>;
}

interface StudioCause {
  code?: unknown;
  reason?: unknown;
  count?: unknown;
}

const FILE_INVALID: Readonly<Record<string, string>> = {
  not_png: 'uploadStudio:error.notPng',
  unreadable: 'uploadStudio:error.unreadable',
  too_big: 'uploadStudio:error.tooBig',
  too_many_pixels: 'uploadStudio:error.tooManyPixels',
  hash_mismatch: 'uploadStudio:error.hashMismatch',
};

const PLACEMENT_CONFLICT: Readonly<Record<string, string>> = {
  shared_changed: 'uploadStudio:error.sharedChanged',
  shared_created: 'uploadStudio:error.sharedCreated',
  excluded: 'uploadStudio:error.excludedType',
};

const PRODUCT_CHANGED: Readonly<Record<string, string>> = {
  slots_changed: 'uploadStudio:changed',
  slot_gone: 'uploadStudio:changed',
  product_gone: 'uploadStudio:error.productGone',
};

/** Codes whose line does not depend on a reason. */
const PLAIN: Readonly<Record<string, string>> = {
  UPLOAD_STUDIO_STORAGE_UNSUPPORTED: 'uploadStudio:error.storageUnsupported',
  UPLOAD_STUDIO_DRIVE_FILE_UNKNOWN: 'uploadStudio:error.driveFileUnknown',
  UPLOAD_STUDIO_SOURCE_MISSING: 'uploadStudio:error.sourceMissing',
  UPLOAD_STUDIO_TOO_MANY_LINKED: 'uploadStudio:shared.tooMany',
  UPLOAD_STUDIO_SLOT_TAKEN: 'uploadStudio:error.slotTaken',
  UPLOAD_STUDIO_LOCATION_CHANGED: 'uploadStudio:error.locationChanged',
  MANUAL_IMAGE_BUSY: 'uploadStudio:error.busy',
  // Product sync's own upload refusals keep Product sync's lines.
  PRODUCT_SYNC_UPLOAD_NEW_PRODUCT: 'productSync:upload.refusal.newProduct',
  PRODUCT_SYNC_UPLOAD_TYPE_NO_CODE: 'productSync:upload.refusal.typeNoCode',
  platen_derive_other_product: 'productSync:platen.derive.refusal.otherProduct',
  platen_derive_source_not_large: 'productSync:platen.derive.refusal.sourceNotLarge',
  platen_derive_no_preset: 'productSync:platen.derive.refusal.noPreset',
  platen_derive_source_missing: 'productSync:platen.derive.refusal.sourceMissing',
  platen_derive_source_changed: 'productSync:platen.derive.refusal.sourceChanged',
  platen_derive_busy: 'productSync:platen.derive.refusal.busy',
  platen_derive_failed: 'productSync:platen.derive.refusal.failed',
};

export const STUDIO_GENERIC_ERROR = 'uploadStudio:error.generic';

function causeOf(err: unknown): StudioCause | null {
  const cause = (err as { data?: { cause?: unknown } } | null | undefined)?.data?.cause;
  return cause && typeof cause === 'object' ? (cause as StudioCause) : null;
}

/** The registered line for a refusal, or null for an error with no registered code. */
export function studioRefusalLine(err: unknown): StudioErrorLine | null {
  const cause = causeOf(err);
  const code = typeof cause?.code === 'string' ? cause.code : null;
  if (!code) return null;
  const reason = typeof cause?.reason === 'string' ? cause.reason : '';
  switch (code) {
    case 'UPLOAD_STUDIO_FILE_INVALID':
      return { key: FILE_INVALID[reason] ?? 'uploadStudio:error.unreadable' };
    case 'UPLOAD_STUDIO_PLACEMENT_CONFLICT':
      return { key: PLACEMENT_CONFLICT[reason] ?? 'uploadStudio:error.sharedChanged' };
    case 'UPLOAD_STUDIO_PRODUCT_CHANGED':
      return { key: PRODUCT_CHANGED[reason] ?? 'uploadStudio:changed' };
    case 'UPLOAD_STUDIO_SHARED_CHANGE_UNCONFIRMED': {
      const count = typeof cause?.count === 'number' ? cause.count : 0;
      return { key: 'uploadStudio:error.sharedUnconfirmed', values: { count } };
    }
    default: {
      const key = PLAIN[code];
      return key ? { key } : null;
    }
  }
}

/** The line to show for any studio error: the registered one, else the generic line (never the server's text). */
export function studioErrorLine(err: unknown): StudioErrorLine {
  return studioRefusalLine(err) ?? { key: STUDIO_GENERIC_ERROR };
}

/**
 * A commit worth sending again with the same upload id and sha256: the
 * request never got an answer (network), or a gateway cut it (502 / 504).
 * The server finds the stored original by its hash, so the retry never reads
 * or uploads the file twice.
 */
export function isRetryableCommitError(err: unknown): boolean {
  const e = err as { data?: { httpStatus?: unknown } | null; meta?: { response?: { status?: unknown } } } | null;
  const status = typeof e?.data?.httpStatus === 'number' ? e.data.httpStatus : e?.meta?.response?.status;
  if (status === 502 || status === 504) return true;
  // No answer at all: no tRPC error data and no HTTP status.
  return !e?.data && typeof status !== 'number';
}
